'use client'

import type {
  PasswordStrength,
  PasswordStrengthContextValue,
  PasswordStrengthProviderProps,
} from '@pearlconvert/ui/providers/password-strength/types'

import { createContext, useCallback, useContext } from 'react'

const initialValue: PasswordStrengthContextValue = {
  getPasswordStrength: () => ({}) as PasswordStrength,
}

const PasswordStrengthContext =
  createContext<PasswordStrengthContextValue>(initialValue)

export const PasswordStrengthProvider = ({
  children,
  strengths,
  calculateProgress,
}: PasswordStrengthProviderProps) => {
  const getPasswordStrength = useCallback(
    (password: string): PasswordStrength => {
      const progress = calculateProgress(password)

      const sortedStrengths = [...strengths].sort(
        (a, b) => b.progress - a.progress
      )

      const matchedStrength =
        sortedStrengths.find((strength) => progress >= strength.progress) ||
        strengths[0]

      return { ...matchedStrength, progress }
    },
    [calculateProgress, strengths]
  )

  return (
    <PasswordStrengthContext.Provider value={{ getPasswordStrength }}>
      {children}
    </PasswordStrengthContext.Provider>
  )
}

export const usePasswordStrength = (): PasswordStrengthContextValue => {
  const context = useContext(PasswordStrengthContext)
  if (!context) {
    throw new Error(
      'usePasswordStrength must be used within a PasswordStrengthProvider'
    )
  }
  return context
}
